/**
 * SAT Survival of the Fittest™ · Assessment Page (STEP 2)
 * Minimaler Smoke-Level Flow analog CircleAssessment.jsx.
 * Nur Capacity 0–10 pro Dimension → Envelope-Release.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem("sat_token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function SurvivalAssessment() {
  const [dimensions, setDimensions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [ratings, setRatings] = useState({}); // key → { capacity }
  const [envelope, setEnvelope] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/survival/registry`);
        setDimensions(r.data.dimensions);
        const init = {};
        r.data.dimensions.forEach(d => { init[d.key] = { capacity: 5 }; });
        setRatings(init);
        const s = await axios.get(`${API}/api/survival/session/current`, { headers: authHeaders() });
        if (s.data.session_id) {
          setSessionId(s.data.session_id);
          s.data.ratings.forEach(r => {
            init[r.dimension_key] = { capacity: r.capacity };
          });
          setRatings({ ...init });
        }
      } catch (e) {
        setError(e?.response?.data?.detail?.message || "Survival ist derzeit nicht verfügbar.");
      }
    })();
  }, []);

  async function startSession() {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/survival/session`, {}, { headers: authHeaders() });
      setSessionId(r.data.session_id);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || String(e));
    }
    setLoading(false);
  }

  function updateRating(key, value) {
    setRatings(prev => ({ ...prev, [key]: { capacity: Number(value) } }));
  }

  async function saveAndRelease() {
    setLoading(true);
    try {
      await axios.post(`${API}/api/survival/ratings`, {
        session_id: sessionId,
        ratings: dimensions.map(d => ({ dimension_key: d.key, capacity: ratings[d.key]?.capacity ?? 5 })),
      }, { headers: authHeaders() });
      const r = await axios.post(`${API}/api/survival/session/${sessionId}/release`, {}, { headers: authHeaders() });
      setEnvelope(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || String(e));
    }
    setLoading(false);
  }

  const avg = useMemo(() => {
    const vals = Object.values(ratings);
    if (!vals.length) return 0;
    return (vals.reduce((s, r) => s + (r.capacity || 0), 0) / vals.length).toFixed(1);
  }, [ratings]);

  return (
    <div className="min-h-screen bg-[#08102A] text-slate-100" data-testid="survival-assessment-page">
      <div className="mx-auto max-w-4xl px-6 py-14">
        <p className="mb-2 text-xs uppercase tracking-widest text-[#D4AF37]">Survival of the Fittest™</p>
        <h1 className="mb-6 text-4xl font-light text-[#F6E7B8] sm:text-5xl">
          Die Architektur deiner Lebensfähigkeit
        </h1>
        <p className="mb-10 max-w-2xl text-slate-300">
          Bewerte für jede der 9 Kapazitäten, wie <span className="text-[#D4AF37]">verfügbar und stabil</span> sie
          sich bei dir unter realen Anforderungen und Druck zeigt.
        </p>

        {error && (
          <div className="mb-6 rounded border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200"
               data-testid="survival-error">{error}</div>
        )}

        {envelope && (
          <div className="mb-8 rounded-2xl border border-[#D4AF37]/30 bg-slate-900/40 p-6"
               data-testid="survival-result">
            <p className="text-sm uppercase tracking-widest text-[#D4AF37]">Deine Lebensfähigkeit</p>
            <p className="mt-2 text-6xl font-light text-white">{envelope.overall_survival_score}<span className="text-2xl text-slate-500">/100</span></p>
            <p className="mt-4 text-sm text-slate-300">Stärkste Kapazitäten: <span className="text-[#D4AF37]">{envelope.strongest_capacities?.join(" · ")}</span></p>
            <p className="mt-1 text-sm text-slate-300">Entwicklungsräume: <span className="text-[#D4AF37]">{envelope.development_capacities?.join(" · ")}</span></p>
            <p className="mt-4 text-xs text-slate-500">Envelope: {envelope.envelope_version} · Hash: {envelope.export_hash?.slice(0, 12)}…</p>
          </div>
        )}

        {!sessionId ? (
          <button onClick={startSession} disabled={loading}
                  data-testid="survival-start-btn"
                  className="rounded-full bg-[#D4AF37] px-6 py-3 text-sm font-medium text-[#08102A] hover:bg-[#F6E7B8]">
            {loading ? "…" : "Survival of the Fittest starten"}
          </button>
        ) : !envelope && (
          <>
            <div className="mb-6 rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-3 text-sm">
              Durchschnittliche Kapazität: <span className="text-[#D4AF37]">{avg}</span>/10
            </div>
            <div className="space-y-4">
              {dimensions.map(d => (
                <div key={d.key} data-testid={`survival-dim-${d.key}`}
                     className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-white">{d.label}</p>
                    <span className="text-[#D4AF37]">{ratings[d.key]?.capacity ?? 5}/10</span>
                  </div>
                  <input type="range" min="0" max="10"
                         value={ratings[d.key]?.capacity ?? 5}
                         onChange={e => updateRating(d.key, e.target.value)}
                         data-testid={`survival-slider-${d.key}`}
                         className="w-full accent-[#D4AF37]" />
                </div>
              ))}
            </div>
            <button onClick={saveAndRelease} disabled={loading}
                    data-testid="survival-release-btn"
                    className="mt-8 rounded-full bg-[#D4AF37] px-6 py-3 text-sm font-medium text-[#08102A] hover:bg-[#F6E7B8] disabled:opacity-50">
              {loading ? "Speichere…" : "Auswertung freigeben"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
